import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faInfoCircle } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import Checkbox from '@/components/ui/Checkbox';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast } from '@/utils/toasts.ts';
import { toCurrencyCentValue } from '@/utils/helpers.ts';
import type { RecommendationSwingtrade, SwingTradeType } from '@/models';

interface EditSwingTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  swingTrade: RecommendationSwingtrade | null;
  onSwingTradeUpdated: (swingTrade: RecommendationSwingtrade) => void;
}

export default function EditSwingTradeModal({
  isOpen,
  onClose,
  swingTrade,
  onSwingTradeUpdated
}: EditSwingTradeModalProps) {
  const [type, setType] = useState<SwingTradeType>('LONG');
  const [takeProfit, setTakeProfit] = useState('');
  const [boost, setBoost] = useState(false);
  const [free, setFree] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (swingTrade) {
      setType(swingTrade.type);
      setTakeProfit((swingTrade.takeProfit / 100).toFixed(2));
      setBoost(swingTrade.boost);
      setFree(swingTrade.free);
    }
  }, [swingTrade]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!swingTrade) return;

    // Validate takeProfit
    if (parseFloat(takeProfit) <= 0) {
      handleAPIError(new Error('Take-Profit Preis muss größer als 0 sein'));
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        type: type,
        takeProfit: toCurrencyCentValue(parseFloat(takeProfit)),
        boost: boost,
        free: free
      };

      const updatedSwingTrade = await APIRoutes.updateSwingTrade(swingTrade.id, requestData);
      onSwingTradeUpdated(updatedSwingTrade);
      showSuccessToast('Swing Trade erfolgreich aktualisiert');
      onClose();
    } catch (error) {
      handleAPIError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!swingTrade) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Swing Trade bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-purple-600" />}
      maxWidth="md"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
            />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Eingeschränkte Bearbeitungsmöglichkeiten</p>
              <p>
                Nur die Handelsrichtung kann hier geändert werden. Verwenden Sie <strong>Adjustments</strong>,
                um Positionen anzupassen, oder <strong>Trade beenden</strong>, um den Trade abzuschließen.
              </p>
            </div>
          </div>
        </div>

        {/* Trade Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Handelsrichtung *
          </label>
          <select
            id="type"
            value={type}
            onChange={(e) => setType(e.target.value as SwingTradeType)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
            disabled={loading}
          >
            <option value="LONG">Long</option>
            <option value="SHORT">Short</option>
          </select>
        </div>

        {/* Take Profit */}
        <div>
          <label htmlFor="takeProfit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Take-Profit Preis  *
          </label>
          <input
            id="takeProfit"
            type="number"
            value={takeProfit}
            onChange={(e) => setTakeProfit(e.target.value)}
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="120.00"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Empfohlener Zielpreis für Take-Profit (nicht funktional)
          </p>
        </div>

        <Checkbox
          checked={boost}
          onChange={setBoost}
          disabled={loading}
          colorTheme="purple"
          label="Als Boost-Trade markieren"
        />

        <Checkbox
          checked={free}
          onChange={setFree}
          disabled={loading}
          colorTheme="purple"
          label="Als kostenlosen Trade markieren"
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
